import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { LocalDate } from "@/components/ui/local-date";
import { Select } from "@/components/ui/select";
import { INPUT_CLS } from "@/lib/analysis";
import { ApiError, api } from "@/lib/api";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { MeetingProjectPicker } from "./meeting-project-picker";

/**
 * The "invite Hubert to the meeting" controls on `/analysis/meetings` —
 * both platforms in one list:
 *
 *   - Discord voice: pick a channel, the bot joins and records until Stop,
 *     then STT + analysis run in the background.
 *   - Google Meet: paste a Meet link (optionally with a start time) and a
 *     bot joins the call and brings back the captions transcript. The
 *     usual trigger is a calendar invite to the shared mailbox — this
 *     dialog is the manual path.
 *
 * The card polls until each meeting lands (completed → link to analysis).
 */

type VoiceChannel = { id: string; name: string; guildName: string };
type Recording = {
  id: string;
  platform: "discord" | "meet";
  channelId: string | null;
  channelName: string | null;
  guildName: string | null;
  meetingUrl: string | null;
  title: string | null;
  joinAt: string | null;
  status:
    | "scheduled"
    | "joining"
    | "recording"
    | "processing"
    | "completed"
    | "failed"
    | "cancelled";
  error: string | null;
  /** What the recording missed, if anything (deaf tail, rejoins, dropped
   *  utterances) — set live while recording and finalised on stop. */
  warning: string | null;
  /** When audio last reached Hubert; null = he hasn't heard anything yet. */
  lastAudioAt: string | null;
  /** Discord only: the meeting's audio is still on disk, so the transcript
   *  can be redone. False once the retention window passed. */
  audioAvailable: boolean;
  utterances: number;
  analysisId: string | null;
  projectId: string | null;
  startedAt: string;
  endedAt: string | null;
};
type Project = { id: string; name: string };

const STATUS_LABEL: Record<Recording["status"], string> = {
  scheduled: "Scheduled",
  joining: "Connecting…",
  recording: "Recording…",
  processing: "Transcribing & analysing…",
  completed: "Completed",
  failed: "Failed",
  cancelled: "Cancelled",
};

/** Substring of the server's NO_JOIN_ERROR — a failed meeting whose bot never
 *  got into the call reads as "Hubert didn't join", not a generic "Failed". */
const NO_JOIN_MARKER = "nepřipojil";

/** Whether a failed row failed because the bot never joined the call. */
function isNoJoin(r: Recording): boolean {
  return r.status === "failed" && (r.error?.includes(NO_JOIN_MARKER) ?? false);
}

/** A Discord voice connection can die without a sound — the bot stays in the
 *  channel and the recording keeps running while nothing reaches it. Past
 *  this much silence the live row says so, so the meeting can be restarted
 *  while it's still happening. */
const SILENT_AFTER_MIN = 3;

/** How long a running Discord recording has heard nothing, or null while
 *  audio is flowing (and always for Meet, whose bot reports differently). */
function silentMinutes(r: Recording, now: number): number | null {
  if (r.platform === "meet" || r.status !== "recording") return null;
  const since = new Date(r.lastAudioAt ?? r.startedAt).getTime();
  const min = Math.floor((now - since) / 60_000);
  return min >= SILENT_AFTER_MIN ? min : null;
}

/** What to call a meeting in the lists: Discord channel or Meet title/link. */
function meetingLabel(r: Recording): string {
  if (r.platform === "meet") {
    return r.title ?? r.meetingUrl?.replace(/^https:\/\//, "") ?? "Google Meet";
  }
  return r.channelName ? `#${r.channelName}` : (r.channelId ?? "Discord");
}

export function VoiceRecorder({
  projects,
  onCompleted,
}: {
  projects: Project[];
  onCompleted?: () => void;
}) {
  const [recordings, setRecordings] = useState<Recording[] | null>(null);
  const [channels, setChannels] = useState<VoiceChannel[] | null>(null);
  const [channelsError, setChannelsError] = useState<string | null>(null);
  const [sttConfigured, setSttConfigured] = useState(true);
  const [channelId, setChannelId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const seenCompleted = useRef<Set<string>>(new Set());

  // Google Meet dialog state.
  const [meetOpen, setMeetOpen] = useState(false);
  const [meetConfigured, setMeetConfigured] = useState<boolean | null>(null);
  const [meetUrl, setMeetUrl] = useState("");
  const [meetJoinAt, setMeetJoinAt] = useState("");
  const [meetProjectId, setMeetProjectId] = useState("");
  const [meetError, setMeetError] = useState<string | null>(null);
  const [meetNotice, setMeetNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ recordings: Recording[] }>("/api/analysis/recordings");
      setRecordings(res.recordings);
      // Tell the page when a recording newly completes so the meetings list
      // underneath picks up the fresh analysis.
      for (const r of res.recordings) {
        if (r.status === "completed" && !seenCompleted.current.has(r.id)) {
          seenCompleted.current.add(r.id);
          if (seenCompleted.current.size > 1) onCompleted?.();
        }
      }
    } catch {
      /* transient — next poll retries */
    }
  }, [onCompleted]);

  // Poll while anything is live; idle check every 15s otherwise.
  const anyLive =
    recordings?.some(
      (r) => r.status === "joining" || r.status === "recording" || r.status === "processing",
    ) ?? false;
  useEffect(() => {
    void load();
    const id = setInterval(() => void load(), anyLive ? 4_000 : 15_000);
    return () => clearInterval(id);
  }, [load, anyLive]);

  async function openPicker(open: boolean) {
    setDialogOpen(open);
    if (!open) return;
    setChannels(null);
    setChannelsError(null);
    try {
      const res = await api.get<{ sttConfigured: boolean; channels: VoiceChannel[] }>(
        "/api/analysis/voice-channels",
      );
      setChannels(res.channels);
      setSttConfigured(res.sttConfigured);
      setChannelId(res.channels[0]?.id ?? "");
    } catch (err) {
      setChannelsError(
        err instanceof ApiError && err.status === 503
          ? "The Discord bot is not configured (DISCORD_BOT_TOKEN)."
          : "Couldn't list voice channels.",
      );
    }
  }

  async function start() {
    const channel = channels?.find((ch) => ch.id === channelId);
    if (!channel) return;
    setBusy(true);
    try {
      await api.post("/api/analysis/recordings", {
        channelId: channel.id,
        channelName: channel.name,
        guildName: channel.guildName,
        projectId: projectId || null,
      });
      setDialogOpen(false);
      await load();
    } catch (err) {
      const detail =
        err instanceof ApiError && err.detail && typeof err.detail === "object"
          ? (err.detail as { detail?: string }).detail
          : undefined;
      setChannelsError(detail ?? "Couldn't start the recording.");
    } finally {
      setBusy(false);
    }
  }

  async function stop(id: string) {
    setBusy(true);
    try {
      await api.post(`/api/analysis/recordings/${encodeURIComponent(id)}/stop`, {});
      await load();
    } catch {
      /* the poll will show the real state */
    } finally {
      setBusy(false);
    }
  }

  async function openMeet(open: boolean) {
    setMeetOpen(open);
    if (!open) return;
    setMeetError(null);
    setMeetNotice(null);
    try {
      const res = await api.get<{ configured: boolean }>("/api/analysis/meet/status");
      setMeetConfigured(res.configured);
    } catch {
      setMeetConfigured(false);
    }
  }

  async function sendMeetBot() {
    setBusy(true);
    setMeetError(null);
    setMeetNotice(null);
    try {
      const res = await api.post<{ id: string; reused?: boolean }>("/api/analysis/meet-bots", {
        meetingUrl: meetUrl.trim(),
        ...(meetJoinAt ? { joinAt: new Date(meetJoinAt).toISOString() } : {}),
        projectId: meetProjectId || null,
      });
      // Already covered (usually: the calendar invite scheduled him first).
      // Say so and keep the dialog open — sending a twin is what jams a call.
      if (res.reused) {
        setMeetNotice("Hubert is already on this call — no second bot was sent.");
        await load();
        return;
      }
      setMeetOpen(false);
      setMeetUrl("");
      setMeetJoinAt("");
      await load();
    } catch (err) {
      const detail =
        err instanceof ApiError && err.detail && typeof err.detail === "object"
          ? (err.detail as { detail?: string }).detail
          : undefined;
      setMeetError(detail ?? "Couldn't send the Meet bot.");
    } finally {
      setBusy(false);
    }
  }

  async function cancelMeet(id: string) {
    setBusy(true);
    try {
      await api.delete(`/api/analysis/meet-bots/${encodeURIComponent(id)}`);
      await load();
    } catch {
      /* the poll will show the real state */
    } finally {
      setBusy(false);
    }
  }

  /** Run transcription + analysis again. Meet re-reads the bot's captions;
   *  Discord re-transcribes the audio Hub kept — either way it's the way back
   *  to a record when the first pass came out wrong or incomplete. */
  async function retry(r: Recording) {
    setBusy(true);
    const url =
      r.platform === "meet"
        ? `/api/analysis/meet-bots/${encodeURIComponent(r.id)}/retry`
        : `/api/analysis/recordings/${encodeURIComponent(r.id)}/retry`;
    try {
      await api.post(url, {});
      await load();
    } catch {
      /* the poll will show the real state */
    } finally {
      setBusy(false);
    }
  }

  /** Whether re-running is possible at all: Meet asks its bot again, Discord
   *  needs the audio to still be inside the retention window. */
  function canRetry(r: Recording): boolean {
    if (r.status !== "completed" && r.status !== "failed") return false;
    if (r.platform === "meet") return !isNoJoin(r);
    return r.audioAvailable;
  }

  const live = recordings?.filter(
    (r) =>
      r.status === "scheduled" ||
      r.status === "joining" ||
      r.status === "recording" ||
      r.status === "processing",
  );
  const recent = recordings?.filter((r) => r.status === "completed" || r.status === "failed");

  return (
    <div className="space-y-3" data-testid="voice-recorder">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Voice meetings</h2>
          <p className="text-sm text-muted-foreground">
            Invite Hubert into a Discord voice channel or a Google Meet call — he records,
            transcribes, and proposes follow-ups. For Meet, adding hub@netlion.cz to the calendar
            invite schedules him automatically.
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={meetOpen} onOpenChange={openMeet}>
            <DialogTrigger asChild>
              <Button type="button" size="sm" variant="outline" data-testid="meet-meeting-open">
                Meet meeting
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Send Hubert to a Google Meet</DialogTitle>
              </DialogHeader>
              {meetConfigured === false && (
                <p className="text-sm text-destructive" data-testid="meet-missing">
                  The Meet bot is not configured — set <code>ATTENDEE_URL</code> and{" "}
                  <code>ATTENDEE_API_KEY</code> on the server first.
                </p>
              )}
              {meetError && (
                <p className="text-sm text-destructive" data-testid="meet-error">
                  {meetError}
                </p>
              )}
              {meetNotice && (
                <p className="text-sm text-muted-foreground" data-testid="meet-notice">
                  {meetNotice}
                </p>
              )}
              <div className="space-y-3">
                <label className="block space-y-1 text-sm">
                  <span className="text-muted-foreground">Google Meet link</span>
                  <input
                    value={meetUrl}
                    onChange={(e) => setMeetUrl(e.target.value)}
                    placeholder="https://meet.google.com/abc-defg-hij"
                    className={INPUT_CLS}
                    data-testid="meet-url-input"
                  />
                </label>
                <label className="block space-y-1 text-sm">
                  <span className="text-muted-foreground">Join at (empty = now)</span>
                  <input
                    type="datetime-local"
                    value={meetJoinAt}
                    onChange={(e) => setMeetJoinAt(e.target.value)}
                    className={INPUT_CLS}
                    data-testid="meet-join-at"
                  />
                </label>
                <div className="block space-y-1 text-sm">
                  <span className="text-muted-foreground">Project (analysis context)</span>
                  <Select
                    aria-label="Project (analysis context)"
                    value={meetProjectId}
                    onChange={setMeetProjectId}
                    options={[
                      { value: "", label: "— no project —" },
                      ...projects.map((p) => ({ value: p.id, label: p.name })),
                    ]}
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    onClick={sendMeetBot}
                    disabled={busy || !meetUrl.trim() || meetConfigured === false}
                    data-testid="send-meet-bot"
                  >
                    {busy ? "Sending…" : meetJoinAt ? "Schedule bot" : "Send bot now"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={dialogOpen} onOpenChange={openPicker}>
            <DialogTrigger asChild>
              <Button
                type="button"
                size="sm"
                disabled={live?.some((r) => r.platform !== "meet" && r.status === "recording")}
                data-testid="record-meeting-open"
              >
                Record meeting
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Record a Discord meeting</DialogTitle>
              </DialogHeader>
              {channelsError && (
                <p className="text-sm text-destructive" data-testid="voice-error">
                  {channelsError}
                </p>
              )}
              {!channelsError && channels === null && (
                <p className="text-sm text-muted-foreground">Loading channels…</p>
              )}
              {!channelsError && channels !== null && !sttConfigured && (
                <p className="text-sm text-destructive" data-testid="stt-missing">
                  Speech-to-text is not configured — set <code>STT_API_KEY</code> on the server
                  first.
                </p>
              )}
              {!channelsError && channels !== null && (
                <div className="space-y-3">
                  <div className="block space-y-1 text-sm">
                    <span className="text-muted-foreground">Voice channel</span>
                    <Select
                      aria-label="Voice channel"
                      data-testid="voice-channel-select"
                      value={channelId}
                      onChange={setChannelId}
                      options={channels.map((ch) => ({
                        value: ch.id,
                        label: `${ch.name} · ${ch.guildName}`,
                      }))}
                    />
                  </div>
                  <div className="block space-y-1 text-sm">
                    <span className="text-muted-foreground">Project (analysis context)</span>
                    <Select
                      aria-label="Project (analysis context)"
                      value={projectId}
                      onChange={setProjectId}
                      options={[
                        { value: "", label: "— no project —" },
                        ...projects.map((p) => ({ value: p.id, label: p.name })),
                      ]}
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      onClick={start}
                      disabled={busy || !channelId || !sttConfigured}
                      data-testid="start-recording"
                    >
                      {busy ? "Joining…" : "Join & record"}
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {live?.map((r) => {
        const silent = silentMinutes(r, Date.now());
        return (
          <div
            key={r.id}
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-primary/40 bg-primary/5 px-3 py-2"
            data-testid="live-recording"
          >
            <div className="flex items-center gap-2 text-sm">
              {r.status === "recording" && (
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
                </span>
              )}
              {r.status === "joining" && (
                <span
                  className="relative flex h-2.5 w-2.5"
                  title="Waiting for Hubert to join the call"
                >
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-500 opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-500" />
                </span>
              )}
              {r.platform === "meet" && <Badge variant="outline">Meet</Badge>}
              <span className="font-medium">
                {meetingLabel(r)}
                {r.platform !== "meet" && r.guildName ? ` (${r.guildName})` : ""}
              </span>
              <span className="text-muted-foreground">
                {r.status === "scheduled" && r.joinAt ? (
                  <>
                    {STATUS_LABEL[r.status]} · joins <LocalDate value={r.joinAt} withTime />
                  </>
                ) : r.status === "joining" ? (
                  <>
                    {STATUS_LABEL[r.status]} · sent <LocalDate value={r.startedAt} withTime />
                  </>
                ) : (
                  <>
                    {STATUS_LABEL[r.status]} · started <LocalDate value={r.startedAt} withTime />
                  </>
                )}
              </span>
              {r.warning ? (
                <span className="text-destructive" data-testid="recording-lost">
                  {r.warning}
                </span>
              ) : (
                silent !== null && (
                  <span
                    className="text-amber-600 dark:text-amber-500"
                    data-testid="recording-silent"
                  >
                    no audio for {silent} min
                  </span>
                )
              )}
            </div>
            {/* Both platforms can be ended by hand — for Meet this is also the
              way out when the bot is left alone in an empty call. */}
            {r.status === "recording" && (
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={() => stop(r.id)}
                disabled={busy}
                data-testid="stop-recording"
              >
                {r.platform === "meet" ? "End & analyse" : "Stop & analyse"}
              </Button>
            )}
            {(r.status === "scheduled" || r.status === "joining") && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => cancelMeet(r.id)}
                disabled={busy}
                data-testid="cancel-meet-bot"
              >
                Cancel
              </Button>
            )}
          </div>
        );
      })}

      {recent && recent.length > 0 && (
        <ul className="divide-y rounded-md border text-sm">
          {recent.slice(0, 5).map((r) => (
            <li
              key={r.id}
              className="flex flex-wrap items-center gap-2 px-3 py-2"
              data-testid="recording-row"
            >
              <span>
                <span className="font-medium">{meetingLabel(r)}</span>{" "}
                <span className="text-xs text-muted-foreground">
                  {r.platform === "meet" ? "Meet · " : ""}
                  <LocalDate value={r.startedAt} withTime /> · {r.utterances} utterance
                  {r.utterances === 1 ? "" : "s"}
                </span>
              </span>
              <span className="ml-auto" />
              <MeetingProjectPicker
                path={`/api/analysis/recordings/${encodeURIComponent(r.id)}`}
                projectId={r.projectId}
                projects={projects}
                onSaved={(projectId) =>
                  setRecordings(
                    (rows) => rows?.map((x) => (x.id === r.id ? { ...x, projectId } : x)) ?? rows,
                  )
                }
              />
              {r.status === "completed" && r.analysisId ? (
                <span className="flex items-center gap-2">
                  {/* A transcript that covers five minutes of an hour-long
                      call must not look like the whole meeting. */}
                  {r.warning && (
                    <Badge
                      variant="outline"
                      className="border-amber-500 text-amber-600 dark:text-amber-500"
                      title={r.warning}
                      data-testid="recording-incomplete"
                    >
                      Incomplete recording
                    </Badge>
                  )}
                  <Link
                    to="/analysis/history/$id"
                    params={{ id: r.analysisId }}
                    className="underline"
                    data-testid="recording-analysis-link"
                  >
                    view analysis
                  </Link>
                  {/* Even a finished meeting can be run again while its audio
                      is kept — the way to a better transcript when the first
                      one came out thin. */}
                  {canRetry(r) && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => retry(r)}
                      data-testid="recording-retry"
                    >
                      Transcribe again
                    </Button>
                  )}
                </span>
              ) : r.status === "failed" ? (
                <span className="flex items-center gap-2">
                  {/* A meeting that came out empty is a question ("why?"), and
                      the answer must not live in a tooltip nobody hovers: a
                      room nobody spoke in and a recording that lost every word
                      it heard both read as "Failed · 0 utterances" otherwise. */}
                  {(r.error ?? r.warning) && (
                    <span className="text-xs text-destructive" data-testid="recording-error">
                      {r.error ?? r.warning}
                    </span>
                  )}
                  <Badge
                    variant="destructive"
                    title={r.error ?? undefined}
                    data-testid={isNoJoin(r) ? "recording-no-join" : "recording-failed"}
                  >
                    {isNoJoin(r) ? "Hubert didn't join" : "Failed"}
                  </Badge>
                  {/* Retry re-runs transcription + analysis — useless when the
                      bot never joined or the audio is already swept, so it's
                      only offered when there's something left to work from. */}
                  {canRetry(r) && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => retry(r)}
                      data-testid="recording-retry"
                    >
                      Retry
                    </Button>
                  )}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
